import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Alert,
  BreadcrumbGroup,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  StatusIndicator,
  ColumnLayout,
  Icon,
} from '@cloudscape-design/components';
import axios from 'axios';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

export default function WorkflowAdd() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const workflowEnabled = isEnabled('workflow');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowType, setWorkflowType] = useState({ label: 'Webhook', value: 'webhook' });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [action, setAction] = useState('');
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [quota, setQuota] = useState({ total: 0, used: 0, remaining: 0, loading: true });

  const workflowQuotaAvailable = quota.remaining > 0;
  const canCreateWorkflow = workflowEnabled && workflowQuotaAvailable;

  // Assignment mode: directly to an agent or reserved on a pack
  const [assignmentMode, setAssignmentMode] = useState({ label: 'Reserve on a pack', value: 'pack' });
  const [agents, setAgents] = useState([]);
  const [packs, setPacks] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);

  const accessToken = user?.access_token || user?.token;

  useEffect(() => {
    const fetchAgents = async () => {
      if (!accessToken) return;
      setLoadingAgents(true);
      try {
        const resp = await axios.get('/api/agents/for-assignment/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setAgents(resp.data.data.map(a => ({ label: a.name || a.id, value: a.id })));
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };

    const fetchPacks = async () => {
      if (!accessToken) return;
      setLoadingPacks(true);
      try {
        const resp = await axios.get('/api/company-packs/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setPacks(resp.data.data
            .filter(p => p.quotas?.workflows?.remaining > 0)
            .map(p => {
              const wfTotal = p.quotas?.workflows?.total || 0;
              const wfUsed = p.quotas?.workflows?.used || 0;
              const statusLabel = p.status === 'inventory' ? 'In stock' : (p.has_agent ? 'Assigned' : 'Active');
              return {
                label: `${p.name} (${wfUsed}/${wfTotal} Workflows) — ${statusLabel}`,
                value: p.id,
                data: p
              };
            }));
        }
      } catch (err) {
        console.error('Error fetching packs:', err);
      } finally {
        setLoadingPacks(false);
      }
    };

    const fetchQuota = async () => {
      if (!accessToken) return;
      try {
        const resp = await axios.get('/api/billing/quota/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const resources = resp.data?.data?.resources || {};
        const wf = resources.workflows || { total: 0, used: 0, remaining: 0 };
        setQuota({ total: wf.total, used: wf.used, remaining: wf.remaining, loading: false });
      } catch (err) {
        console.error('Error fetching quota:', err);
        setQuota(prev => ({ ...prev, loading: false }));
      }
    };

    fetchAgents();
    fetchPacks();
    fetchQuota();
  }, [accessToken]);

  // Hidden defaults
  const platformMode = 'dedicated';
  const billingCycle = 'yearly';
  const status = 'pending';

  const workflowTypeOptions = [
    { label: 'Webhook', value: 'webhook' },
    { label: 'MCP', value: 'mcp' },
  ];

  const paramTypeOptions = [
    { label: 'String', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' },
  ];

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Workflow name is required');
      return;
    }
    if (assignmentMode.value === 'agent' && !selectedAgent) {
      setError('Please select an agent');
      return;
    }
    if (assignmentMode.value === 'pack' && !selectedPack) {
      setError('Please select a pack');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const isMcp = workflowType.value === 'mcp';
      const configJson = {
        mode: 'DEDICATED',
        billing_cycle: 'year',
        status: 'pending',
        workflow_type: isMcp ? 'mcp' : 'webhook',
      };
      if (isMcp) {
        configJson.mcp_config = {};
        if (webhookUrl.trim()) configJson.mcp_config.mcp_server_url = webhookUrl.trim();
        if (apiKey.trim()) configJson.mcp_config.mcp_api_key = apiKey.trim();
      } else {
        if (webhookUrl.trim()) configJson.webhook_url = webhookUrl.trim();
        if (apiKey.trim()) configJson.api_key = apiKey.trim();
      }
      if (action.trim()) configJson.action = action.trim();
      const inputs = {};
      params.forEach((p) => {
        if (p.key.trim()) {
          inputs[p.key.trim()] = p.defaultValue;
        }
      });
      if (Object.keys(inputs).length > 0) configJson.inputs = inputs;

      const payload = {
        name: name.trim(),
        description: description.trim(),
        service_type: isMcp ? 'mcp_tool' : 'workflow',
        mcp_enabled: isMcp,
        platform_mode: platformMode,
        billing_cycle: billingCycle,
        status: status,
        config_json: configJson,
      };
      if (assignmentMode.value === 'agent') {
        payload.agent_id = selectedAgent.value;
      } else {
        payload.company_pack_id = selectedPack.value;
      }

      const resp = await axios.post('/api/workflows/create/', payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      let createResp = resp.data;
      if (createResp?.status === 'confirmation_required') {
        payload.confirm_resource_activation = true;
        const confirmResp = await axios.post('/api/workflows/create/', payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        createResp = confirmResp.data;
      }
      if (createResp?.status === 'sync_failed') {
        setError(createResp.message || 'Workflow created locally but failed to sync to adexcloud. You can retry sync from the workflow list.');
        setSuccess(false);
        setTimeout(() => navigate('/workflows'), 3000);
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/workflows'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create workflow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Workflows', href: '/workflows' },
            { text: 'Add Workflow', href: '/workflow/new' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Add Workflow',
        description: 'Create a new workflow for your AI agents.',
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Workflows"
      helpPanelContent={
        <div>
          <p>Workflows connect your agents to external tools, APIs, and automation logic.</p>
          <ul>
            <li>Browser automation</li>
            <li>Code execution</li>
            <li>Custom API integrations</li>
          </ul>
        </div>
      }
    >
      <SpaceBetween size="l">
        {success && (
          <Alert type="success" header="Workflow created">
            Redirecting to workflow list...
          </Alert>
        )}
        {!workflowEnabled && (
          <Alert type="warning" header="Feature disabled">
            Workflows are currently disabled by the administrator.
          </Alert>
        )}
        {!quota.loading && !workflowQuotaAvailable && workflowEnabled && (
          <Alert type="warning" header="Quota exceeded">
            Workflow quota exceeded ({quota.used}/{quota.total}). Purchase a pack with more workflows.
          </Alert>
        )}
        {error && <Alert type="error" header="Error">{error}</Alert>}

        <Container header={<Header variant="h2"><Icon name="script" variant="normal" /> Workflow configuration</Header>}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => navigate('/workflows')}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={!canCreateWorkflow}>
                  Create Workflow
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <ColumnLayout columns={{ default: 1, s: 2 }}>
                <FormField label="Workflow Name" constraintText="Required">
                  <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder="e.g., Booking API" />
                </FormField>
                <FormField label="Type">
                  <Select
                    selectedOption={workflowType}
                    onChange={({ detail }) => setWorkflowType(detail.selectedOption)}
                    options={workflowTypeOptions}
                  />
                </FormField>
              </ColumnLayout>

              <FormField label="Description">
                <Textarea
                  value={description}
                  onChange={({ detail }) => setDescription(detail.value)}
                  placeholder="What does this workflow do?"
                />
              </FormField>

              <Container header={<Header variant="h3">Assignment</Header>}>
                <SpaceBetween size="l">
                  <FormField label="Assign To" description="Associate this workflow with an existing agent or reserve it on a pack.">
                    <Select
                      selectedOption={assignmentMode}
                      onChange={({ detail }) => {
                        setAssignmentMode(detail.selectedOption);
                        setSelectedAgent(null);
                        setSelectedPack(null);
                      }}
                      options={[
                        { label: 'Reserve on a pack', value: 'pack' },
                        { label: 'Existing agent', value: 'agent' },
                      ]}
                    />
                  </FormField>
                  {assignmentMode.value === 'agent' && (
                    <FormField label="Select Agent" description="Choose an agent that will use this workflow.">
                      <Select
                        selectedOption={selectedAgent}
                        onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
                        options={agents}
                        loading={loadingAgents}
                        placeholder="Select an agent"
                      />
                    </FormField>
                  )}
                  {assignmentMode.value === 'pack' && (
                    <FormField label="Select Pack" description="Reserve this workflow on a pack. It can be assigned to an agent later, but must match the same pack.">
                      <Select
                        selectedOption={selectedPack}
                        onChange={({ detail }) => setSelectedPack(detail.selectedOption)}
                        options={packs}
                        loading={loadingPacks}
                        placeholder="Select a pack"
                      />
                    </FormField>
                  )}
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h3">Connection</Header>}>
                <SpaceBetween size="l">
                  <FormField label={workflowType.value === 'mcp' ? 'MCP Server URL' : 'Webhook URL'}>
                    <Input
                      value={webhookUrl}
                      onChange={({ detail }) => setWebhookUrl(detail.value)}
                      placeholder={workflowType.value === 'mcp' ? 'https://sms.adexgenie.ai/mcp' : 'https://api.example.com/webhook'}
                    />
                  </FormField>
                  <ColumnLayout columns={{ default: 1, s: 2 }}>
                    <FormField label="API Key (Authorization header)" description="Optional. Sent in the request headers or MCP config.">
                      <Input
                        value={apiKey}
                        onChange={({ detail }) => setApiKey(detail.value)}
                        placeholder="Bearer sk-... or your-api-key"
                        type="password"
                      />
                    </FormField>
                    <FormField label="Action" description="e.g. send_sms, book_appointment">
                      <Input
                        value={action}
                        onChange={({ detail }) => setAction(detail.value)}
                        placeholder="send_sms"
                      />
                    </FormField>
                  </ColumnLayout>
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h3">Parameters</Header>}>
                <SpaceBetween size="s">
                  {params.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 2 }}>
                        <FormField label={idx === 0 ? "Key" : null}>
                          <Input
                            value={p.key}
                            onChange={({ detail }) => {
                              const next = [...params];
                              next[idx].key = detail.value;
                              setParams(next);
                            }}
                            placeholder="to"
                          />
                        </FormField>
                      </div>
                      <div style={{ flex: 2 }}>
                        <FormField label={idx === 0 ? "Type" : null}>
                          <Select
                            selectedOption={paramTypeOptions.find((o) => o.value === p.type) || paramTypeOptions[0]}
                            onChange={({ detail }) => {
                              const next = [...params];
                              next[idx].type = detail.selectedOption.value;
                              setParams(next);
                            }}
                            options={paramTypeOptions}
                          />
                        </FormField>
                      </div>
                      <div style={{ flex: 3 }}>
                        <FormField label={idx === 0 ? "Default value" : null}>
                          <Input
                            value={p.defaultValue}
                            onChange={({ detail }) => {
                              const next = [...params];
                              next[idx].defaultValue = detail.value;
                              setParams(next);
                            }}
                            placeholder="{{forward_number}}"
                          />
                        </FormField>
                      </div>
                      <div>
                        <Button
                          variant="icon"
                          iconName="remove"
                          onClick={() => setParams((prev) => prev.filter((_, i) => i !== idx))}
                          ariaLabel="Remove parameter"
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="inline-icon"
                    iconName="add-plus"
                    onClick={() => setParams((prev) => [...prev, { key: '', type: 'string', defaultValue: '' }])}
                  >
                    Add parameter
                  </Button>
                </SpaceBetween>
              </Container>
              {loading && <StatusIndicator type="loading">Creating workflow...</StatusIndicator>}
            </SpaceBetween>
          </Form>
        </Container>
      </SpaceBetween>
    </AppLayoutWrapper>
  );
}
